import React from 'react';
import { ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
    label?: string;
    error?: string;
    helperText?: string;
    options: SelectOption[];
    placeholder?: string;
    onChange?: (value: string) => void;
    variant?: 'default' | 'filled';
    selectSize?: 'sm' | 'md' | 'lg';
}

export const Select: React.FC<SelectProps> = ({
                                                  label,
                                                  error,
                                                  helperText,
                                                  options,
                                                  placeholder,
                                                  onChange,
                                                  variant = 'default',
                                                  selectSize = 'md',
                                                  className,
                                                  id,
                                                  required,
                                                  disabled,
                                                  value,
                                                  ...props
                                              }) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    const sizeClasses = {
        sm: 'px-3 py-2 text-sm',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-3 text-base'
    };

    const variantClasses = {
        default: 'bg-white border-gray-300',
        filled: 'bg-gray-50 border-gray-200'
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (onChange) {
            onChange(e.target.value);
        }
    };

    return (
        <div className={clsx('w-full', className)}>
            {label && (
                <label
                    htmlFor={selectId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div className="relative">
                <select
                    id={selectId}
                    value={value}
                    onChange={handleChange}
                    className={clsx(
                        'block w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors appearance-none pr-10',
                        sizeClasses[selectSize],
                        variantClasses[variant],
                        error && 'border-red-300 focus:ring-red-500 focus:border-red-500',
                        disabled && 'bg-gray-50 text-gray-500 cursor-not-allowed'
                    )}
                    disabled={disabled}
                    {...props}
                >
                    {placeholder && (
                        <option value="" disabled>
                            {placeholder}
                        </option>
                    )}
                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </option>
                    ))}
                </select>

                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
            </div>

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}

            {helperText && !error && (
                <p className="mt-1 text-sm text-gray-500">{helperText}</p>
            )}
        </div>
    );
};